import { useMemo } from "react";
import { mediaIdsFromHtml } from "#app/lib/media-content";
import { useFeatureFlags, FeatureFlagKey } from "#app/hooks/use-feature-flags";
import type { CustomFieldDef } from "#app/components/custom-fields/types";
import type { NoteNode } from "#app/components/issue-view/activity/activity-types";
import type { Omittable } from "@salestrak/ui";

/** One piece of media somewhere on the issue, tagged with where it lives.
 * `contentType` drives lightbox rendering; field/inline media are always
 * images, note footer media carries its real type. */
export type IssueMediaItem = (
  | { photoId: string; source: "field"; field: CustomFieldDef }
  | { photoId: string; source: "note" | "note_inline"; note: NoteNode }
) &
  // `fileName` is read straight off a nullable media field, and every consumer
  // spells the miss `?? …` — an absent key and an explicit `undefined` are the
  // same answer, so the member relaxes.
  Omittable<{ contentType: string; fileName?: string | null }>;

/**
 * Aggregates every photo attached to the issue for the Media tab: photos-type
 * custom field values, note footer attachments, and inline note images.
 * Field photos come first (matching the summary tab's order), then notes in
 * feed order with replies nested under their thread.
 */
export function useIssueMediaItems(
  customFields: CustomFieldDef[],
  values: Record<string, unknown> | null,
  notes: NoteNode[],
): IssueMediaItem[] {
  // Workspace-scoped Photos flag: when off, photos custom field values are
  // excluded from the media tab (note photos still show).
  const { hasFlag } = useFeatureFlags();
  const photosEnabled = hasFlag(FeatureFlagKey.PHOTOS);

  return useMemo(() => {
    const items: IssueMediaItem[] = [];

    for (const field of customFields) {
      if (!photosEnabled) break;
      if (field.type !== "photos") continue;
      const value = values?.[field.customFieldId];
      if (!Array.isArray(value)) continue;
      for (const photoId of value) {
        if (typeof photoId === "string") {
          items.push({ photoId, source: "field", field, contentType: "image/*" });
        }
      }
    }

    const walk = (note: NoteNode) => {
      for (const photoId of mediaIdsFromHtml(note.note ?? "")) {
        items.push({ photoId, source: "note_inline", note, contentType: "image/*" });
      }
      for (const media of note.media ?? []) {
        items.push({
          photoId: media.id,
          source: "note",
          note,
          contentType: media.contentType,
          fileName: media.fileName,
        });
      }
      note.replies?.forEach(walk);
    };
    notes.forEach(walk);

    return items;
  }, [customFields, values, notes, photosEnabled]);
}
